<script setup>
import { computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { isWorkspaceV2Path } from 'dashboard/helper/workspaceV2';
import ConversationView from './ConversationView.vue';
import SavedFilterLoadError from 'dashboard/components-next/filter/SavedFilterLoadError.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  foldersId: { type: [String, Number], required: true },
  conversationId: { type: [String, Number], default: 0 },
});
const route = useRoute();
const router = useRouter();
const store = useStore();
const folders = useMapGetter('customViews/getConversationCustomViews');
const flags = useMapGetter('customViews/getUIFlags');
const folderExists = computed(() =>
  folders.value.some(folder => folder.id === Number(props.foldersId))
);

async function resolveFolder() {
  if (folderExists.value) {
    return;
  }
  const prefix = isWorkspaceV2Path(route.path) ? '/v2' : '';
  const destination = props.conversationId
    ? `/conversations/${props.conversationId}`
    : '/dashboard';
  await router.replace({
    path: `/app/accounts/${route.params.accountId}${prefix}${destination}`,
    query: route.query,
  });
}

watch(
  () => flags.value.isFetchingConversation,
  (fetching, wasFetching) => {
    if (wasFetching && !fetching && !flags.value.hasConversationError)
      resolveFolder();
  }
);

watch(
  () => [route.params.accountId, props.foldersId],
  () => {
    if (folderExists.value) {
      return;
    }
    store.dispatch('customViews/get', 'conversation');
  },
  { immediate: true }
);
</script>

<template>
  <ConversationView
    v-if="folderExists"
    :folders-id="foldersId"
    :conversation-id="conversationId"
  />
  <section v-else class="flex h-full w-full items-center justify-center p-4">
    <SavedFilterLoadError filter-type="conversation" />
    <Spinner v-if="!flags.hasConversationError" />
  </section>
</template>
